import React, { useEffect } from 'react';
import { X, ExternalLink, CheckCircle2, Tag, ChevronLeft, ChevronRight, Layers, Cpu, Activity, ShieldCheck } from 'lucide-react';
import { Project } from '../types/portfolio';

interface ProjectModalProps {
  project: Project | null;
  allProjects: Project[];
  onClose: () => void;
  onSelectProject: (project: Project) => void;
}

export const ProjectModal: React.FC<ProjectModalProps> = ({
  project,
  allProjects,
  onClose,
  onSelectProject,
}) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (project) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.body.style.overflow = 'auto';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [project, onClose]);

  if (!project) return null;

  const currentIndex = allProjects.findIndex((p) => p.id === project.id);
  const prevProject = allProjects[(currentIndex - 1 + allProjects.length) % allProjects.length];
  const nextProject = allProjects[(currentIndex + 1) % allProjects.length];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-container glass-card" onClick={(e) => e.stopPropagation()}>
        {/* Navigation & Header Controls */}
        <div className="modal-header-bar">
          <div className="header-meta">
            <span className="modal-cat-badge">{project.category}</span>
            {project.clientName && <span className="modal-client-badge"><ShieldCheck size={14} /> {project.clientName}</span>}
          </div>

          <div className="header-actions">
            <div className="nav-arrows">
              <button
                className="nav-arrow-btn"
                onClick={() => onSelectProject(prevProject)}
                title={`Previous: ${prevProject.name}`}
              >
                <ChevronLeft size={18} />
              </button>
              <span className="nav-indicator">{currentIndex + 1} / {allProjects.length}</span>
              <button
                className="nav-arrow-btn"
                onClick={() => onSelectProject(nextProject)}
                title={`Next: ${nextProject.name}`}
              >
                <ChevronRight size={18} />
              </button>
            </div>

            <button className="modal-close-btn" onClick={onClose} aria-label="Close modal">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Hero Image Header */}
        <div className="modal-hero">
          <img
            src={project.image}
            alt={project.name}
            className="modal-hero-img"
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/assets/images/projects/work-dummy.png';
            }}
          />
          <div className="modal-hero-overlay">
            <h2 className="hero-project-title">{project.name}</h2>
          </div>
        </div>

        {/* Modal Content Details */}
        <div className="modal-content-body">
          {/* Key Metrics Grid */}
          {project.metrics && project.metrics.length > 0 && (
            <div className="metrics-grid">
              {project.metrics.map((m, idx) => (
                <div key={idx} className="metric-box glass-card">
                  <span className="metric-value">{m.value}</span>
                  <span className="metric-label">{m.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Detailed Overview */}
          <div className="detail-section">
            <h4 className="detail-heading">
              <Layers size={18} /> Executive Summary & Case Study
            </h4>
            <p className="detail-text">{project.description}</p>
          </div>

          {/* Key Highlights */}
          {project.highlights && project.highlights.length > 0 && (
            <div className="detail-section">
              <h4 className="detail-heading">
                <Activity size={18} /> Key Technical Accomplishments
              </h4>
              <ul className="highlights-list">
                {project.highlights.map((item, idx) => (
                  <li key={idx}>
                    <CheckCircle2 size={18} className="check-icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* System Architecture */}
          {project.architecture && (
            <div className="detail-section">
              <h4 className="detail-heading">
                <Cpu size={18} /> System Architecture & Infrastructure
              </h4>
              <div className="architecture-box glass-card">
                <p>{project.architecture}</p>
              </div>
            </div>
          )}

          {/* Tech Stack Badges */}
          <div className="detail-section">
            <h4 className="detail-heading">
              <Tag size={18} /> Technology Stack & Tools
            </h4>
            <div className="tech-pills">
              {project.technologies.map((tech) => (
                <span key={tech} className="tech-pill">
                  {tech}
                </span>
              ))}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="modal-footer-actions">
            {project.link ? (
              <a
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary action-cta"
              >
                <ExternalLink size={18} />
                <span>Launch Live Application</span>
              </a>
            ) : (
              <div className="enterprise-status-badge">
                <ShieldCheck size={18} />
                <span>Enterprise Internal Application</span>
              </div>
            )}
            <button className="btn btn-secondary" onClick={onClose}>
              Close Preview
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .modal-backdrop {
          position: fixed;
          inset: 0;
          z-index: 2500;
          background: rgba(9, 13, 22, 0.88);
          backdrop-filter: blur(16px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.5rem;
          animation: modalFadeIn 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .modal-container {
          position: relative;
          width: 100%;
          max-width: 780px;
          max-height: 90vh;
          overflow-y: auto;
          background: var(--bg-secondary);
          border: 1px solid var(--border-highlight);
          border-radius: var(--radius-lg);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7);
          display: flex;
          flex-direction: column;
        }

        .modal-header-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.25rem 1.75rem;
          border-bottom: 1px solid var(--border-color);
          background: var(--bg-glass);
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .header-meta {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .modal-cat-badge {
          padding: 0.3rem 0.85rem;
          border-radius: var(--radius-full);
          background: var(--gradient-brand);
          color: #fff;
          font-size: 0.8rem;
          font-weight: 700;
        }

        .modal-client-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.3rem 0.85rem;
          border-radius: var(--radius-full);
          background: rgba(99, 102, 241, 0.12);
          border: 1px solid var(--border-highlight);
          color: var(--accent-primary);
          font-size: 0.8rem;
          font-weight: 600;
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .nav-arrows {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: var(--bg-tertiary);
          padding: 0.25rem 0.6rem;
          border-radius: var(--radius-full);
          border: 1px solid var(--border-color);
        }

        .nav-arrow-btn {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s ease;
        }

        .nav-arrow-btn:hover {
          color: var(--accent-primary);
        }

        .nav-indicator {
          font-family: var(--font-mono);
          font-size: 0.78rem;
          color: var(--text-dim);
          font-weight: 600;
        }

        .modal-close-btn {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          color: var(--text-main);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .modal-close-btn:hover {
          color: var(--accent-primary);
          border-color: var(--border-highlight);
          transform: scale(1.1);
        }

        .modal-hero {
          position: relative;
          width: 100%;
          height: 280px;
          overflow: hidden;
          background: var(--bg-tertiary);
        }

        .modal-hero-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .modal-hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, var(--bg-secondary) 0%, rgba(15, 23, 42, 0.4) 60%, transparent 100%);
          display: flex;
          align-items: flex-end;
          padding: 2rem 1.75rem;
        }

        .hero-project-title {
          font-size: 1.85rem;
          font-weight: 800;
          color: var(--text-main);
          letter-spacing: -0.02em;
          line-height: 1.2;
        }

        .modal-content-body {
          padding: 1.75rem;
          display: flex;
          flex-direction: column;
          gap: 1.75rem;
        }

        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1rem;
        }

        .metric-box {
          padding: 1rem 1.25rem;
          text-align: center;
          display: flex;
          flex-direction: column;
          background: var(--bg-tertiary);
        }

        .metric-value {
          font-size: 1.5rem;
          font-weight: 800;
          color: var(--accent-secondary);
          font-family: var(--font-mono);
        }

        .metric-label {
          font-size: 0.8rem;
          color: var(--text-muted);
          font-weight: 600;
          text-transform: uppercase;
        }

        .detail-heading {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 1.05rem;
          font-weight: 700;
          color: var(--text-main);
          margin-bottom: 0.85rem;
        }

        .detail-text {
          font-size: 1rem;
          color: var(--text-muted);
          line-height: 1.7;
        }

        .highlights-list {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .highlights-list li {
          display: flex;
          align-items: flex-start;
          gap: 0.65rem;
          font-size: 0.95rem;
          color: var(--text-muted);
          line-height: 1.5;
        }

        .check-icon {
          color: var(--accent-secondary);
          flex-shrink: 0;
          margin-top: 2px;
        }

        .architecture-box {
          padding: 1.25rem;
          background: var(--bg-tertiary);
          border-left: 3px solid var(--accent-primary);
          font-size: 0.95rem;
          color: var(--text-muted);
          line-height: 1.65;
        }

        .tech-pills {
          display: flex;
          flex-wrap: wrap;
          gap: 0.6rem;
        }

        .tech-pill {
          padding: 0.4rem 0.9rem;
          border-radius: var(--radius-sm);
          background: rgba(99, 102, 241, 0.12);
          border: 1px solid var(--border-highlight);
          color: var(--text-main);
          font-size: 0.85rem;
          font-weight: 600;
        }

        .modal-footer-actions {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-top: 1.5rem;
          border-top: 1px solid var(--border-color);
          gap: 1rem;
          flex-wrap: wrap;
        }

        .action-cta {
          padding: 0.85rem 2rem;
          font-size: 1rem;
        }

        .enterprise-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.7rem 1.25rem;
          border-radius: var(--radius-full);
          background: rgba(16, 185, 129, 0.12);
          border: 1px solid rgba(16, 185, 129, 0.3);
          color: var(--accent-success);
          font-weight: 700;
          font-size: 0.9rem;
        }

        @keyframes modalFadeIn {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }

        @media (max-width: 576px) {
          .modal-hero {
            height: 200px;
          }
          .hero-project-title {
            font-size: 1.4rem;
          }
          .modal-content-body {
            padding: 1.25rem;
          }
        }
      `}</style>
    </div>
  );
};
